import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const parsePage = (value, fallback) => {
  const parsed = parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * Drop-in replacement for `useState(defaults)` for table search/filter state that
 * keeps the current `page` in sync with the URL query string. This lets the active
 * page survive a full page refresh (and browser back/forward) instead of resetting
 * to page 1.
 *
 * Usage:
 *   const [searchQuery, setSearchQuery] = usePersistedSearchQuery({ page: 1, itemsPerPage: 10 });
 *
 * @param {object} defaults - Initial search query object (must contain a `page` key).
 * @param {object} [options]
 * @param {string} [options.paramKey='page'] - Query param name. Use a unique key when
 *   multiple paginated tables can be mounted on the same route at once.
 * @returns {[object, Function]} - The standard `[state, setState]` tuple.
 */
const usePersistedSearchQuery = (defaults = {}, options = {}) => {
  const { paramKey = 'page' } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultPage = defaults.page ?? 1;

  const [searchQuery, setSearchQuery] = useState(() => ({
    ...defaults,
    page: parsePage(searchParams.get(paramKey), defaultPage),
  }));

  const pageRef = useRef(searchQuery.page);
  pageRef.current = searchQuery.page;

  // Write state.page -> URL whenever it changes.
  useEffect(() => {
    const urlPage = parsePage(searchParams.get(paramKey), defaultPage);
    if (urlPage === searchQuery.page) return;

    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (!searchQuery.page || searchQuery.page <= 1) {
          next.delete(paramKey);
        } else {
          next.set(paramKey, String(searchQuery.page));
        }
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery.page]);

  // Sync URL -> state.page for browser back/forward navigation.
  useEffect(() => {
    const urlPage = parsePage(searchParams.get(paramKey), defaultPage);
    if (urlPage !== pageRef.current) {
      setSearchQuery(prev => ({ ...prev, page: urlPage }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return [searchQuery, setSearchQuery];
};

export default usePersistedSearchQuery;
